import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { pdfjs } from "react-pdf";
import { useTranslation } from 'react-i18next';
import { citationBbox, clusterCitationHighlights } from './citationHighlightGeometry';
import './ContractCitationHighlights.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const ZOOM_CONFIG = {
    MIN: 0.5,
    MAX: 3,
    STEP: 0.2,
    DEFAULT: 1.0,
};

const isContractProfileHighlightBlock = (block) => (
    !!block?.citation || block?.kind === "contract_profile_citation" || block?.kind === "section_anchor"
);

const originalCitationBlocks = blocks => blocks.filter(block => block.kind === 'resolved_source_citation');

const getHighlightGroupCountLabel = (group) => {
    if (!Array.isArray(group)) return "";
    if (group.some(isContractProfileHighlightBlock)) return "";
    return String(group.length);
};

const buildPdfFile = (fileUrl) => {
    if (!fileUrl) return null;
    if (/^https?:\/\//i.test(String(fileUrl))) return fileUrl;
    const clean = fileUrl.includes(",") ? fileUrl.split(",")[1] : fileUrl;
    const raw = atob(clean);
    const uint8 = new Uint8Array(raw.length);
    for (let index = 0; index < raw.length; index += 1) {
        uint8[index] = raw.charCodeAt(index);
    }
    return {data: uint8};
};

function ContractViewerV2({
                              fileUrl,
                              textractBlocks = [],
                              hoveredRef,
                              selectedRef,
                              onBlockHover,
                              onBlockClick,
                              isAnalyzing = false,
                              requestedPage = null,
                              requestedCitationId = null,
                              navigationKey = null,
                              onPageCount,
                              onLoadError,
                              isActive = true,
                          }) {
    const { t } = useTranslation('review');
    const [numPages, setNumPages] = useState(null);
    const [scale, setScale] = useState(null);        // null = pas encore calculé
    const [autoScale, setAutoScale] = useState(1.0);
    const containerRef = useRef(null);
    const pageRefs = useRef({});
    const bboxRefs = useRef({});
    const pendingSelectedScrollRef = useRef(null);
    const forceScrollCleanupRef = useRef(null);
    const navigationGenerationRef = useRef(0);
    const pdfClickSelectionRef = useRef(null);
    const liveNavigationRef = useRef({});
    liveNavigationRef.current = {textractBlocks, requestedPage, requestedCitationId, selectedRef, isActive};
    const [pageDimensions, setPageDimensions] = useState({});
    const [pageRenderTick, setPageRenderTick] = useState(0);
    const [pdfNativeWidth, setPdfNativeWidth] = useState(595);

    // ⚡ Extraction: conversion base64
    // ✅ `react-pdf` accepte un objet { data: Uint8Array } — plus robuste qu'une string base64
    const pdfFile = useMemo(() => {
        if (!fileUrl) return null;
        try {
            return buildPdfFile(fileUrl);
        } catch (err) {
            console.error("Erreur de décodage base64 :", err);
            return null;
        }
    }, [fileUrl]);

    // Fit-to-width keeps the PDF readable without horizontal scroll and preserves bbox alignment.
    const computeFitScale = useCallback((nativeWidth = pdfNativeWidth) => {
        if (!containerRef.current) return null;
        const containerWidth = containerRef.current.clientWidth;
        if (containerWidth < 100) return null; // container pas encore rendu
        const marginH = 32;  // marges horizontales (16px * 2)

        return Math.min(
            ZOOM_CONFIG.MAX,
            Math.max(ZOOM_CONFIG.MIN, (containerWidth - marginH) / nativeWidth)
        );
    }, [pdfNativeWidth]);

    // ✅ Calcule le scale initial via ResizeObserver (attend que le container ait sa vraie taille)
    useEffect(() => {
        if (!containerRef.current) return;
        
        const trySetScale = () => {
            if (scale !== null) return; // déjà calculé
            const fit = computeFitScale();
            if (fit !== null) {
                setScale(fit);
                setAutoScale(fit);
            }
        };

        // Essaie immédiatement
        trySetScale();
        
        // Puis observe les changements de taille pour attraper le bon moment
        const resizeObserver = new ResizeObserver(() => {
            trySetScale();
        });
        
        resizeObserver.observe(containerRef.current);
        
        return () => resizeObserver.disconnect();
    }, [computeFitScale, scale]);
    useEffect(() => {
        // Reset dur quand on change de doc/version
        setNumPages(null);
        setPageDimensions({});
        setPageRenderTick(0);
        setScale(null);
        pageRefs.current = {};
        bboxRefs.current = {};
        pendingSelectedScrollRef.current = null;
    }, [fileUrl]);

    const adjustRefForMobileSheet = useCallback((targetEl, behavior = "smooth") => {
        if (typeof document === "undefined" || typeof window === "undefined") return 0;
        const shell = document.querySelector(".review-mobile-sheet-shell");
        const sheet = document.querySelector(".review-mobile-bottom-sheet");
        const viewer = containerRef.current;
        if (!shell || !sheet || !viewer || !targetEl) return 0;

        const expandedSheet = sheet.querySelector(".review-mobile-sheet-expanded");
        if (!expandedSheet) return 0;

        const generation = navigationGenerationRef.current;
        const adjustOnce = () => {
            if (generation !== navigationGenerationRef.current || !liveNavigationRef.current.isActive) return;
            const sheetRect = expandedSheet.getBoundingClientRect();
            const targetRect = targetEl.getBoundingClientRect();
            const viewerRect = viewer.getBoundingClientRect();
            const comfortGap = window.innerWidth <= 390 ? 34 : 42;
            const desiredBottom = Math.min(sheetRect.top, viewerRect.bottom) - comfortGap;
            const requiredDelta = targetRect.bottom - desiredBottom;

            if (requiredDelta > 8) {
                viewer.scrollBy({
                    top: Math.min(Math.round(requiredDelta), Math.round(viewer.clientHeight * 0.72)),
                    behavior,
                });
            }
        };

        const delays = behavior === "smooth" ? [260, 620, 980] : [0];
        delays.forEach((delay) => window.setTimeout(adjustOnce, delay));
    }, []);

    const scrollToRegisteredRef = useCallback((ref, behavior = "smooth") => {
        if (!ref || !bboxRefs.current) return false;
        if (!liveNavigationRef.current.isActive) return false;
        const entries = [...(bboxRefs.current[ref]?.values() || [])]
            .filter(entry => entry.el?.isConnected).sort((a, b) => a.page - b.page || a.top - b.top || a.left - b.left);
        const allBlocks = liveNavigationRef.current.textractBlocks.filter(block => block.ref === ref && citationBbox(block.bbox));
        const requestedCitation = liveNavigationRef.current.requestedCitationId;
        const citationBlocks = requestedCitation ? allBlocks.filter(block => block.source_citation_id === requestedCitation || block.citation_id === requestedCitation) : [];
        const blocks = citationBlocks.length ? citationBlocks : allBlocks;
        const candidateEntries = citationBlocks.length ? entries.filter(entry => entry.citationIds.includes(requestedCitation)) : entries;
        const requested = Number(liveNavigationRef.current.requestedPage);
        const preferredPage = blocks.some(block => block.page === requested) ? requested : Math.min(...blocks.map(block => block.page));
        const entry = Number.isFinite(preferredPage) ? candidateEntries.find(item => item.page === preferredPage) : candidateEntries[0];
        const el = entry?.el;
        if (el) {
            el.scrollIntoView({
                behavior,
                block: "center",
            });
            adjustRefForMobileSheet(el, behavior);
            if (pendingSelectedScrollRef.current === ref) {
                pendingSelectedScrollRef.current = null;
            }
            return true;
        }
        return false;
    }, [adjustRefForMobileSheet]);

    const scheduleScrollToRef = useCallback((ref, delays = [0, 120, 300, 650, 1100, 1800, 2800]) => {
        if (!ref) return () => {};
        navigationGenerationRef.current += 1;
        pendingSelectedScrollRef.current = ref;

        const timers = [];
        const clearTimers = () => {
            timers.forEach((timer) => clearTimeout(timer));
            timers.length = 0;
        };
        const tryScroll = () => {
            if (pendingSelectedScrollRef.current !== ref) return false;
            if (scrollToRegisteredRef(ref)) {
                clearTimers();
                return true;
            }
            return false;
        };

        if (tryScroll()) return () => {};

        delays.forEach((delay) => {
            timers.push(setTimeout(tryScroll, delay));
        });

        return () => {
            clearTimers();
            if (pendingSelectedScrollRef.current === ref) pendingSelectedScrollRef.current = null;
        };
    }, [scrollToRegisteredRef]);

    // Scroll vers la bbox sélectionnée quand selectedRef change. In comparison
    // mode, the PDF/highlight layer can mount after the citation click, so this
    // must tolerate late bbox registration.
    useEffect(() => {
        if (!selectedRef || !isActive) return undefined;
        const pdfSelection = pdfClickSelectionRef.current;
        if (pdfSelection?.ref === selectedRef && pdfSelection.navigationKey === navigationKey) return undefined;
        pdfClickSelectionRef.current = null;
        return scheduleScrollToRef(selectedRef);
    }, [scheduleScrollToRef, selectedRef, navigationKey, requestedCitationId, fileUrl, isActive]);

    useEffect(() => {
        const pendingRef = pendingSelectedScrollRef.current;
        if (!pendingRef || !isActive) return;
        scrollToRegisteredRef(pendingRef);
    }, [pageRenderTick, pageDimensions, scrollToRegisteredRef, isActive]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !isActive) return undefined;
        const cancelPending = () => {
            pendingSelectedScrollRef.current = null;
            navigationGenerationRef.current += 1;
        };
        const cancelOnKey = event => {
            if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) cancelPending();
        };
        ['wheel', 'touchstart', 'pointerdown'].forEach(type => container.addEventListener(type, cancelPending, {passive: true}));
        container.addEventListener('keydown', cancelOnKey);
        return () => {
            ['wheel', 'touchstart', 'pointerdown'].forEach(type => container.removeEventListener(type, cancelPending));
            container.removeEventListener('keydown', cancelOnKey);
        };
    }, [isActive]);

    // ⚡ Handlers séparés et clairs
    const handleDocumentLoadSuccess = async (pdf) => {
        setNumPages(pdf.numPages);
        onPageCount?.(pdf.numPages);
        setCurrentPage(1);
        programmaticPageScrollRef.current = null;
        if (containerRef.current && isActive && !selectedRef && !requestedPage) {
            containerRef.current.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
        
        // Récupère la vraie largeur native de la première page du PDF
        try {
            const firstPage = await pdf.getPage(1);
            const viewport = firstPage.getViewport({ scale: 1 });
            const nativeWidth = viewport.width;
            setPdfNativeWidth(nativeWidth);
            
            // Recalcule le fit avec la vraie largeur native de la page.
            const fitScale = computeFitScale(nativeWidth);
            if (fitScale !== null) {
                setAutoScale(fitScale);
                setScale(fitScale);
            }
        } catch (err) {
            console.error("Erreur récupération dimensions PDF:", err);
            // Fallback sur la valeur par défaut
            const fitScale = computeFitScale();
            if (fitScale !== null) {
                setAutoScale(fitScale);
                setScale(fitScale);
            }
        }
    };

    const handleZoom = (delta) => {
        setScale((prev) => {
            const newScale = Math.min(ZOOM_CONFIG.MAX, Math.max(ZOOM_CONFIG.MIN, prev + delta));
            setTimeout(() => {
                Object.keys(pageRefs.current).forEach((pageNum) => {
                    const page = pageRefs.current[pageNum];
                    if (!page) return;
                    const canvas = page.querySelector("canvas");
                    if (canvas) {
                        const rect = canvas.getBoundingClientRect();
                        page.querySelectorAll("[data-ref]").forEach((el) => {
                            // The render layer stores its display bounds. Looking
                            // up the first raw line by ref would undo a paragraph
                            // outline (and select the wrong page) after zoom.
                            const box = el.dataset.displayBbox ? JSON.parse(el.dataset.displayBbox) : null;
                            if (box) {
                                const left = box.left * rect.width;
                                const top = box.top * rect.height;
                                const width = box.width * rect.width;
                                const height = box.height * rect.height;
                                el.style.left = `${left}px`;
                                el.style.top = `${top}px`;
                                el.style.width = `${width}px`;
                                el.style.height = `${height}px`;
                            }
                        });
                    }
                });
            }, 300);
            return newScale;
        });
    };

    useEffect(() => {
        if (!isActive) return undefined;
        const handleForceScroll = (event) => {
            const ref = event.detail;
            if (!ref) return;
            forceScrollCleanupRef.current?.();
            forceScrollCleanupRef.current = scheduleScrollToRef(ref, [0, 150, 350, 700, 1200, 2000, 3200]);
        };

        window.addEventListener("force-scroll", handleForceScroll);
        return () => {
            forceScrollCleanupRef.current?.();
            window.removeEventListener("force-scroll", handleForceScroll);
        };
    }, [scheduleScrollToRef, isActive]);

    // ⚡ Extraction: calcul des styles de bbox
    // Calcule le pourcentage de chevauchement entre deux bboxes
    const calculateOverlap = (bbox1, bbox2) => {
        const x1 = Math.max(bbox1.left, bbox2.left);
        const y1 = Math.max(bbox1.top, bbox2.top);
        const x2 = Math.min(bbox1.left + bbox1.width, bbox2.left + bbox2.width);
        const y2 = Math.min(bbox1.top + bbox1.height, bbox2.top + bbox2.height);

        if (x2 <= x1 || y2 <= y1) return 0;

        const intersectionArea = (x2 - x1) * (y2 - y1);
        const bbox1Area = bbox1.width * bbox1.height;

        return intersectionArea / bbox1Area;
    };

    // ⚡ Groupe les blocs qui se chevauchent
    const normalizeViewerBbox = (bbox = {}) => {
        const normalized = {
            left: Number(bbox.left ?? bbox.Left),
            top: Number(bbox.top ?? bbox.Top),
            width: Number(bbox.width ?? bbox.Width),
            height: Number(bbox.height ?? bbox.Height),
        };

        const isValid = ["left", "top", "width", "height"].every((key) => Number.isFinite(normalized[key]))
            && normalized.width > 0
            && normalized.height > 0;

        return isValid ? normalized : null;
    };

    const getUnionBbox = (blocks) => {
        const boxes = blocks.map((block) => normalizeViewerBbox(block?.bbox)).filter(Boolean);
        if (boxes.length === 0) return null;

        const left = Math.min(...boxes.map((bbox) => bbox.left));
        const top = Math.min(...boxes.map((bbox) => bbox.top));
        const right = Math.max(...boxes.map((bbox) => bbox.left + bbox.width));
        const bottom = Math.max(...boxes.map((bbox) => bbox.top + bbox.height));

        return {
            left,
            top,
            width: right - left,
            height: bottom - top,
        };
    };

    const getHorizontalOverlapRatio = (bbox1, bbox2) => {
        const left = Math.max(bbox1.left, bbox2.left);
        const right = Math.min(bbox1.left + bbox1.width, bbox2.left + bbox2.width);
        if (right <= left) return 0;

        return (right - left) / Math.min(bbox1.width, bbox2.width);
    };

    const shouldMergeParagraphLines = (currentCluster, nextBlock) => {
        const clusterBbox = getUnionBbox(currentCluster);
        const nextBbox = normalizeViewerBbox(nextBlock?.bbox);
        if (!clusterBbox || !nextBbox) return false;

        const clusterBottom = clusterBbox.top + clusterBbox.height;
        const nextBottom = nextBbox.top + nextBbox.height;
        const verticalGap = Math.max(0, Math.max(clusterBbox.top, nextBbox.top) - Math.min(clusterBottom, nextBottom));
        const averageLineHeight = clusterBbox.height / currentCluster.length;
        const maxLineHeight = Math.max(averageLineHeight, nextBbox.height);
        const horizontalOverlap = getHorizontalOverlapRatio(clusterBbox, nextBbox);
        const leftAligned = Math.abs(clusterBbox.left - nextBbox.left) <= 0.035;
        const rightAligned = Math.abs((clusterBbox.left + clusterBbox.width) - (nextBbox.left + nextBbox.width)) <= 0.035;

        return verticalGap <= maxLineHeight * 1.45 && (horizontalOverlap >= 0.25 || leftAligned || rightAligned);
    };

    const mergeParagraphHighlightBlocks = (blocks) => {
        const groupsByRefAndPage = new Map();

        blocks.forEach((block) => {
            const bbox = normalizeViewerBbox(block?.bbox);
            if (!block?.ref || !bbox) return;

            const key = `${block.page || 1}::${block.ref}`;
            const nextBlock = { ...block, bbox };
            const existing = groupsByRefAndPage.get(key) || [];
            existing.push(nextBlock);
            groupsByRefAndPage.set(key, existing);
        });

        return Array.from(groupsByRefAndPage.values()).flatMap((sameRefBlocks) => {
            if (sameRefBlocks.length <= 1) return sameRefBlocks;

            const sorted = [...sameRefBlocks].sort((a, b) => {
                const topDelta = a.bbox.top - b.bbox.top;
                return Math.abs(topDelta) > 0.001 ? topDelta : a.bbox.left - b.bbox.left;
            });
            const clusters = [];

            sorted.forEach((block) => {
                const currentCluster = clusters[clusters.length - 1];
                if (currentCluster && shouldMergeParagraphLines(currentCluster, block)) {
                    currentCluster.push(block);
                    return;
                }
                clusters.push([block]);
            });

            return clusters
                .map((cluster) => ({
                    ...cluster[0],
                    bbox: getUnionBbox(cluster),
                    mergedHighlightCount: cluster.length,
                }))
                .filter((block) => block.bbox);
        });
    };

    const groupOverlappingBlocks = (blocks) => {
        const OVERLAP_THRESHOLD = 0.4; // 40% de chevauchement
        const groups = [];
        const processed = new Set();
        const validBlocks = blocks.filter((block) => {
            const bbox = normalizeViewerBbox(block?.bbox);
            if (!bbox) return false;
            block.bbox = bbox;
            return true;
        });

        validBlocks.forEach((block, i) => {
            if (processed.has(i)) return;

            const group = [block];
            processed.add(i);

            // Trouve tous les blocs qui chevauchent celui-ci
            for (let j = i + 1; j < validBlocks.length; j++) {
                if (processed.has(j)) continue;

                const other = validBlocks[j];
                const overlap = calculateOverlap(block.bbox, other.bbox);

                if (overlap > OVERLAP_THRESHOLD) {
                    group.push(other);
                    processed.add(j);
                }
            }

            groups.push(group);
        });

        return groups;
    };

    // ⚡ Rendu d'un groupe de bboxes (plusieurs commentaires sur la même zone)
    const renderBboxGroup = (group, i, width, height, citationClusters = []) => {
        const firstBlock = group[0];
        const left = firstBlock.bbox.left * width;
        const top = firstBlock.bbox.top * height;
        const boxWidth = firstBlock.bbox.width * width;
        const boxHeight = firstBlock.bbox.height * height;

        const hasSelected = group.some((b) => b.ref === selectedRef);
        const hasHovered = group.some((b) => b.ref === hoveredRef);
        const isMulti = group.length > 1;
        const isSourceCitation = firstBlock.kind === 'resolved_source_citation';
        const countLabel = getHighlightGroupCountLabel(group);

        const highlightClassName = [
            "vf-pdf-highlight",
            hasSelected ? "is-selected" : "",
            hasHovered ? "is-hovered" : "",
            firstBlock.kind === "section_anchor" ? "is-section-anchor" : "",
            isMulti ? "is-multi" : "",
            group.some(isContractProfileHighlightBlock) ? "is-contract-profile-cluster" : "",
            isSourceCitation ? 'is-source-citation-cluster' : '',
        ].filter(Boolean).join(" ");

        return (
            <div
                key={firstBlock.displayClusterKey || `group-${i}`}
                className={highlightClassName}
                ref={(el) => {
                    group.forEach((item) => {
                        if (!item?.ref) return;
                        const key = firstBlock.displayClusterKey || `${firstBlock.page}:${i}`;
                        const entries = bboxRefs.current[item.ref] || new Map();
                        if (el) entries.set(key, {el, page: firstBlock.page, top: firstBlock.bbox.top, left: firstBlock.bbox.left,
                            citationIds: [...new Set((firstBlock.sourceBlocks || group).flatMap(part => [part.source_citation_id, part.citation_id]).filter(Boolean))]});
                        else entries.delete(key);
                        if (entries.size) bboxRefs.current[item.ref] = entries;
                        else delete bboxRefs.current[item.ref];
                        if (el && pendingSelectedScrollRef.current === item.ref) {
                            // Ref registration can happen long after the click,
                            // when the PDF or navigation data finishes loading.
                            Promise.resolve().then(() => {
                                if (pendingSelectedScrollRef.current === item.ref) scrollToRegisteredRef(item.ref);
                            });
                        }
                    });
                }}
                data-ref={`group-${group[0].ref}`}
                data-display-bbox={JSON.stringify(firstBlock.bbox)}
                data-original-rect-count={firstBlock.sourceBlocks?.length}
                onClick={(event) => {
                    let refs = [...new Set(group.map(block => block.ref))];
                    if (isSourceCitation) {
                        const rect = event.currentTarget.getBoundingClientRect();
                        const x = rect.width > 0 ? firstBlock.bbox.left + ((event.clientX - rect.left) / rect.width) * firstBlock.bbox.width
                            : firstBlock.bbox.left + firstBlock.bbox.width / 2;
                        const y = rect.height > 0 ? firstBlock.bbox.top + ((event.clientY - rect.top) / rect.height) * firstBlock.bbox.height
                            : firstBlock.bbox.top + firstBlock.bbox.height / 2;
                        // Separate source groups keep their own bounds. Only
                        // interaction cycles comments covering the clicked point.
                        refs = [...new Set(citationClusters.filter(block => x >= block.bbox.left && x <= block.bbox.left + block.bbox.width
                            && y >= block.bbox.top && y <= block.bbox.top + block.bbox.height).map(block => block.ref))];
                        if (!refs.length) refs = [firstBlock.ref];
                    }
                    const nextRef = refs[(refs.indexOf(selectedRef) + 1) % refs.length];
                    if (isSourceCitation) pdfClickSelectionRef.current = {ref: nextRef, navigationKey};
                    onBlockClick?.(nextRef);
                }}
                onMouseEnter={() => onBlockHover?.(isSourceCitation ? firstBlock.ref : selectedRef ?? group[0].ref)}
                onMouseLeave={() => onBlockHover?.(null)}
                style={{
                    position: "absolute",
                    left,
                    top,
                    width: boxWidth,
                    height: boxHeight,
                    pointerEvents: "auto",
                    zIndex: hasSelected ? 100 : hasHovered ? 50 : 5,
                }}
            >
                {isMulti && countLabel && (
                    <div
                        className="vf-pdf-highlight-count"
                        title={countLabel}
                    >
                        {countLabel}
                    </div>
                )}
            </div>
        );
    };

    // ⚡ Rendu de toutes les bboxes d'une page
    const renderBoundingBoxes = (pageNumber, width, height) => {
        const blocksOnPage = textractBlocks.filter((b) => b.page === pageNumber && b.bbox);
        // Source rectangles stay canonical. Nearby lines get one display-only
        // paragraph outline; source groups/pages/documents never coalesce.
        const originals = clusterCitationHighlights(originalCitationBlocks(blocksOnPage));
        const paragraphBlocks = mergeParagraphHighlightBlocks(blocksOnPage.filter(block => block.kind !== 'resolved_source_citation'));
        const groups = [...groupOverlappingBlocks(paragraphBlocks), ...originals.map(block => [block])];

        return groups.map((group, i) => renderBboxGroup(group, i, width, height, originals));
    };

    // ⚡ Extraction: rendu d'une page complète
    const renderPage = (pageNumber) => (
        <div 
            key={`page_${pageNumber}`} 
            className="pdf-pageRow"
            style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 16,
            }}
        >
            <div
                className="pdf-pageShell"
                ref={(el) => (pageRefs.current[pageNumber] = el)}
                style={{
                    position: "relative",
                    borderRadius: 4,
                    overflow: "hidden",
                    '--page-index': pageNumber - 1,
                }}
            >
                <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onRenderSuccess={() => {
                        const canvas = pageRefs.current[pageNumber]?.querySelector("canvas");
                        if (canvas) {
                            const rect = canvas.getBoundingClientRect();
                            setPageDimensions((prev) => {
                                const existing = prev[pageNumber];
                                const next = { width: rect.width, height: rect.height };

                                // ✅ si rien n’a changé, on retourne EXACTEMENT prev (pas de nouvel objet)
                                if (existing && existing.width === next.width && existing.height === next.height) {
                                    return prev;
                                }
                                return { ...prev, [pageNumber]: next };
                            });
                        }
                        setPageRenderTick((tick) => tick + 1);
                    }}
                />

                {pageDimensions[pageNumber]?.width > 0 && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                        }}
                    >
                        {renderBoundingBoxes(pageNumber, pageDimensions[pageNumber].width, pageDimensions[pageNumber].height)}
                    </div>
                )}
            </div>
        </div>
    );

    // ⚡ Extraction: contrôles de zoom
    const [showZoom, setShowZoom] = useState(false);
    const zoomTimeoutRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const programmaticPageScrollRef = useRef(null);
    const pageScrollSettleTimeoutRef = useRef(null);

    useEffect(() => () => {
        if (pageScrollSettleTimeoutRef.current) {
            clearTimeout(pageScrollSettleTimeoutRef.current);
        }
    }, []);

    useEffect(() => {
        programmaticPageScrollRef.current = null;
        if (pageScrollSettleTimeoutRef.current) {
            clearTimeout(pageScrollSettleTimeoutRef.current);
            pageScrollSettleTimeoutRef.current = null;
        }
        setCurrentPage(1);
        const resetScrollTop = () => {
            const container = containerRef.current;
            if (container && liveNavigationRef.current.isActive && !liveNavigationRef.current.selectedRef && !liveNavigationRef.current.requestedPage) {
                container.scrollTo({ top: 0, left: 0, behavior: "auto" });
            }
        };

        resetScrollTop();
        const frameId = window.requestAnimationFrame(resetScrollTop);
        return () => window.cancelAnimationFrame(frameId);
    }, [fileUrl]);

    useEffect(() => {
        if (!numPages) return;
        setCurrentPage((page) => Math.max(1, Math.min(numPages, page || 1)));
    }, [numPages]);

    const getPageScrollTop = (container, pageEl) => {
        const containerRect = container.getBoundingClientRect();
        const pageRect = pageEl.getBoundingClientRect();
        return Math.max(0, container.scrollTop + pageRect.top - containerRect.top - 12);
    };

    const scrollToPage = (pageNumber) => {
        if (!liveNavigationRef.current.isActive) return;
        const container = containerRef.current;
        const nextPage = Math.max(1, Math.min(numPages, pageNumber));
        const pageEl = pageRefs.current[nextPage];
        if (!container || !pageEl || !numPages) return;

        programmaticPageScrollRef.current = nextPage;
        if (pageScrollSettleTimeoutRef.current) {
            clearTimeout(pageScrollSettleTimeoutRef.current);
        }
        setCurrentPage(nextPage);
        container.scrollTo({
            top: getPageScrollTop(container, pageEl),
            behavior: "smooth",
        });
        pageScrollSettleTimeoutRef.current = setTimeout(() => {
            programmaticPageScrollRef.current = null;
            pageScrollSettleTimeoutRef.current = null;
        }, 650);
    };

    const hasPreciseSelectedPage = textractBlocks.some(block => block.kind === 'resolved_source_citation' && block.ref === selectedRef
        && block.page === Number(requestedPage) && citationBbox(block.bbox));
    useEffect(() => {
        const page = Number(requestedPage);
        if (!isActive || !Number.isFinite(page) || page < 1 || !numPages) return undefined;
        // A PDF-origin selection keeps the clicked viewport even if the last
        // card navigation still carries a page belonging to another citation.
        if (pdfClickSelectionRef.current?.navigationKey === navigationKey) return undefined;
        // A precise citation owns the paragraph position. Page fallback must
        // never override its successful scroll a few hundred milliseconds later.
        if (hasPreciseSelectedPage) return undefined;
        const generation = navigationGenerationRef.current;
        const timers = [0, 160, 420].map((delay) => setTimeout(() => {
            if (generation === navigationGenerationRef.current) scrollToPage(page);
        }, delay));
        return () => timers.forEach(clearTimeout);
    // navigationKey intentionally retriggers navigation to the same page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigationKey, numPages, requestedPage, isActive, hasPreciseSelectedPage]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !numPages || !isActive) return undefined;

        let frameId = null;

        const updateCurrentPage = () => {
            frameId = null;

            const expectedPage = programmaticPageScrollRef.current;
            if (expectedPage !== null) {
                const expectedEl = pageRefs.current[expectedPage];
                const expectedTop = expectedEl ? getPageScrollTop(container, expectedEl) : null;
                if (expectedTop !== null && Math.abs(container.scrollTop - expectedTop) > 4) {
                    return;
                }
                programmaticPageScrollRef.current = null;
            }

            if (container.scrollTop <= 8) {
                setCurrentPage((previousPage) => (previousPage === 1 ? previousPage : 1));
                return;
            }

            const containerRect = container.getBoundingClientRect();
            const viewportTop = containerRect.top;
            const viewportBottom = containerRect.bottom;
            const viewportCenter = viewportTop + (containerRect.height / 2);
            let nextPage = 1;
            let bestVisibleArea = 0;
            let bestDistance = Number.POSITIVE_INFINITY;

            for (let pageNumber = 1; pageNumber <= numPages; pageNumber += 1) {
                const pageEl = pageRefs.current[pageNumber];
                if (!pageEl) continue;

                const pageRect = pageEl.getBoundingClientRect();
                if (pageRect.height <= 0) continue;

                const visibleTop = Math.max(pageRect.top, viewportTop);
                const visibleBottom = Math.min(pageRect.bottom, viewportBottom);
                const visibleArea = Math.max(0, visibleBottom - visibleTop);
                const pageCenter = pageRect.top + (pageRect.height / 2);
                const distance = Math.abs(pageCenter - viewportCenter);

                if (visibleArea > bestVisibleArea || (visibleArea === bestVisibleArea && distance < bestDistance)) {
                    bestVisibleArea = visibleArea;
                    bestDistance = distance;
                    nextPage = pageNumber;
                }
            }

            setCurrentPage((previousPage) => (
                previousPage === nextPage ? previousPage : nextPage
            ));
        };

        const requestUpdate = () => {
            if (frameId !== null) return;
            frameId = window.requestAnimationFrame(updateCurrentPage);
        };

        requestUpdate();
        container.addEventListener("scroll", requestUpdate, { passive: true });
        window.addEventListener("resize", requestUpdate);

        return () => {
            container.removeEventListener("scroll", requestUpdate);
            window.removeEventListener("resize", requestUpdate);
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, [numPages, scale, pageRenderTick, isActive]);

    const handleMouseEnterViewer = () => {
        clearTimeout(zoomTimeoutRef.current);
        setShowZoom(true);
    };
    const handleMouseLeaveViewer = () => {
        zoomTimeoutRef.current = setTimeout(() => setShowZoom(false), 1200);
    };

    const ZoomControls = () => (
        <div
            className="pdf-floating-toolbar"
            style={{
                position: "absolute",
                top: 10,
                left: "50%",
                transform: `translateX(-50%) translateY(${showZoom ? "0" : "-8px"})`,
                zIndex: 20,
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "5px 10px",
                background: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(8px)",
                borderRadius: "10px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
                border: "1px solid rgba(0,0,0,0.06)",
                opacity: showZoom ? 1 : 0,
                pointerEvents: showZoom ? "auto" : "none",
                transition: "opacity 0.25s ease, transform 0.25s ease",
            }}
            onMouseEnter={() => { clearTimeout(zoomTimeoutRef.current); setShowZoom(true); }}
            onMouseLeave={handleMouseLeaveViewer}
        >
            <button
                className="pdf-floating-toolbar__button"
                onClick={() => handleZoom(-ZOOM_CONFIG.STEP)}
                style={{
                    width: 30,
                    height: 30,
                    cursor: "pointer",
                    border: "none",
                    background: "transparent",
                    borderRadius: "6px",
                    fontSize: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#404040",
                    transition: "background 0.15s ease",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f0f0f0"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                aria-label="Zoom out"
            >
                −
            </button>

            <span className="pdf-floating-toolbar__scale" style={{
                padding: "2px 4px",
                minWidth: "44px",
                textAlign: "center",
                fontSize: 12,
                fontWeight: 600,
                color: "#404040",
                userSelect: "none",
            }}>
                {Math.round(scale * 100)}%
            </span>

            <button
                className="pdf-floating-toolbar__button"
                onClick={() => handleZoom(+ZOOM_CONFIG.STEP)}
                style={{
                    width: 30,
                    height: 30,
                    cursor: "pointer",
                    border: "none",
                    background: "transparent",
                    borderRadius: "6px",
                    fontSize: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#404040",
                    transition: "background 0.15s ease",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f0f0f0"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                aria-label="Zoom in"
            >
                +
            </button>

            <div className="pdf-floating-toolbar__divider" style={{ width: 1, height: 18, background: "#e0e0e0", margin: "0 4px" }} />

            <button
                className="pdf-floating-toolbar__button pdf-floating-toolbar__fit"
                onClick={() => setScale(autoScale || ZOOM_CONFIG.DEFAULT)}
                style={{
                    height: 30,
                    padding: "0 12px",
                    cursor: "pointer",
                    border: "none",
                    background: "transparent",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#404040",
                    display: "flex",
                    alignItems: "center",
                    transition: "background 0.15s ease",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f0f0f0"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                aria-label="Fit to width"
                title="Fit width"
            >
                {t('tabs.fit_width', 'Ajuster')}
            </button>

            {numPages ? (
                <>
                    <div className="pdf-floating-toolbar__divider" style={{ width: 1, height: 18, background: "#e0e0e0", margin: "0 4px" }} />

                    <button
                        className="pdf-floating-toolbar__button"
                        onClick={() => scrollToPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                        style={{
                            width: 30,
                            height: 30,
                            cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                            border: "none",
                            background: "transparent",
                            borderRadius: "6px",
                            fontSize: 14,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: currentPage <= 1 ? "#bfbfbf" : "#404040",
                            transition: "background 0.15s ease",
                        }}
                        onMouseEnter={e => {
                            if (currentPage <= 1) return;
                            e.currentTarget.style.background = "#f0f0f0";
                        }}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        aria-label={t('tabs.previous_page', 'Page precedente')}
                        title={t('tabs.previous_page', 'Page precedente')}
                    >
                        ←
                    </button>

                    <span className="pdf-floating-toolbar__page-status" style={{
                        padding: "2px 8px",
                        minWidth: "92px",
                        textAlign: "center",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#404040",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                    }}>
                        {t('tabs.page_status', 'Page {{current}} / {{total}}', {
                            current: currentPage,
                            total: numPages,
                        })}
                    </span>

                    <button
                        className="pdf-floating-toolbar__button"
                        onClick={() => scrollToPage(currentPage + 1)}
                        disabled={currentPage >= numPages}
                        style={{
                            width: 30,
                            height: 30,
                            cursor: currentPage >= numPages ? "not-allowed" : "pointer",
                            border: "none",
                            background: "transparent",
                            borderRadius: "6px",
                            fontSize: 14,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: currentPage >= numPages ? "#bfbfbf" : "#404040",
                            transition: "background 0.15s ease",
                        }}
                        onMouseEnter={e => {
                            if (currentPage >= numPages) return;
                            e.currentTarget.style.background = "#f0f0f0";
                        }}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        aria-label={t('tabs.next_page', 'Page suivante')}
                        title={t('tabs.next_page', 'Page suivante')}
                    >
                        →
                    </button>
                </>
            ) : null}
        </div>
    );
    return (
        <div
            className="pdf-viewer-shell"
            style={{ position: "relative", height: "calc(100vh - 140px)" }}
            onMouseEnter={handleMouseEnterViewer}
            onMouseLeave={handleMouseLeaveViewer}
        >
            {scale !== null && <ZoomControls />}
            <div
                ref={containerRef}
                className={`pdf-viewer ${isAnalyzing ? 'is-ai-reading' : ''}`}
                style={{ 
                    height: "100%", 
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "16px 0",
                    position: "relative",
                }}
            >
                {fileUrl && scale !== null ? (
                    <Document
                        key={fileUrl}
                        file={pdfFile || fileUrl}
                        onLoadSuccess={handleDocumentLoadSuccess}
                        onLoadError={onLoadError || ((e) => console.error("react-pdf load error", e))}
                        className="pdf-document-centered"
                    >
                        {numPages && Array.from({ length: numPages }, (_, i) => renderPage(i + 1))}
                    </Document>
                ) : null}
            </div>
            <div className="pdf-context-overlay" aria-hidden="true" />
        </div>
    );
}

export default ContractViewerV2;

export const __testing = {
    buildPdfFile,
    getHighlightGroupCountLabel,
    isContractProfileHighlightBlock,
    originalCitationBlocks,
};

